import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DateTime } from 'luxon';

// 🎨 GlobalDateStore – furnizor centralizat de dată/timp la nivel de secundă
// ✨ UPGRADED: Dual UTC/Local state, timezone management, testing utilities
// 🔧 REFACTORED: Helper functions moved to utils/dateHelpers.ts for clean architecture
interface GlobalDateState {
  /** Data şi ora curentă (BACKWARD COMPATIBILITY) */
  currentDateTime: Date;
  /** Data şi ora curentă în UTC (NEW) */
  currentDateTimeUTC: Date;
  /** Data şi ora curentă în timezone-ul local/preferat (NEW) */
  currentDateTimeLocal: Date;
  /** Fus orar preferat (default: browser timezone) */
  timezone: string;
  /** Frecvenţa de actualizare în milisecunde (default: 1000 – 1 secundă) */
  updateFrequency: number;
  /** Flag pentru testing mode (NEW) */
  isTestMode: boolean;

  // ⚙️ TIMER ACTIONS
  /** Porneşte actualizarea automată a timpului */
  startClock: () => void;
  /** Opreşte actualizarea automată a timpului */
  stopClock: () => void;
  /** Schimbă fusul orar preferat */
  setTimezone: (timezone: string) => void;
  /** Schimbă frecvenţa de actualizare */
  setUpdateFrequency: (frequency: number) => void;

  // 🧪 TESTING UTILITIES (NEW)
  /** Setează manual timpul pentru teste (NEW) */
  setCurrentDateTime: (date: Date) => void;
  /** Avansează timpul cu X milisecunde pentru teste (NEW) */
  advanceTime: (milliseconds: number) => void;
}

let clockTimer: NodeJS.Timeout | null = null;

export const useGlobalDateStore = create<GlobalDateState>()(
  persist(
    (set, get) => ({
      // 📊 STATE INIȚIAL
      currentDateTime: new Date(),
      currentDateTimeUTC: new Date(),
      currentDateTimeLocal: new Date(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      updateFrequency: 1000,
      isTestMode: false,

      // ⚙️ TIMER MANAGEMENT
      startClock: () => {
        const { updateFrequency } = get();
        
        // Clear any existing timer pentru a evita multiple timers
        if (clockTimer) clearInterval(clockTimer);
        
        clockTimer = setInterval(() => {
          const now = new Date();
          const { timezone } = get();
          
          // Calculează UTC și Local timezone pentru dual state
          const utcDate = new Date(now.getTime());
          const localDate = DateTime.fromJSDate(now).setZone(timezone).toJSDate();
          
          set({
            currentDateTime: now,
            currentDateTimeUTC: utcDate,
            currentDateTimeLocal: localDate,
          });
        }, updateFrequency);
      },

      stopClock: () => {
        if (clockTimer) {
          clearInterval(clockTimer);
          clockTimer = null;
        }
      },

      // 🌍 TIMEZONE MANAGEMENT
      setTimezone: (timezone: string) => {
        const { currentDateTime } = get();
        const localDate = DateTime.fromJSDate(currentDateTime).setZone(timezone).toJSDate();
        
        set({
          timezone,
          currentDateTimeLocal: localDate,
        });
      },

      setUpdateFrequency: (frequency: number) => {
        set({ updateFrequency: frequency });
        
        // Restart clock cu noua frecvență dacă este pornit
        if (clockTimer) {
          get().stopClock();
          get().startClock();
        }
      },

      // 🧪 TESTING UTILITIES
      setCurrentDateTime: (date: Date) => {
        const { timezone } = get();
        const utcDate = new Date(date.getTime());
        const localDate = DateTime.fromJSDate(date).setZone(timezone).toJSDate();
        
        set({
          currentDateTime: date,
          currentDateTimeUTC: utcDate,
          currentDateTimeLocal: localDate,
          isTestMode: true,
        });
      },

      advanceTime: (milliseconds: number) => {
        const { currentDateTime, timezone } = get();
        const newDateTime = new Date(currentDateTime.getTime() + milliseconds);
        const utcDate = new Date(newDateTime.getTime());
        const localDate = DateTime.fromJSDate(newDateTime).setZone(timezone).toJSDate();
        
        set({
          currentDateTime: newDateTime,
          currentDateTimeUTC: utcDate,
          currentDateTimeLocal: localDate,
          isTestMode: true,
        });
      },
    }),
    {
      name: 'global-date-store',
      // Persist doar timezone preference, nu state-ul temporar
      partialize: (state) => ({ timezone: state.timezone }),
    }
  )
);

// =============================================================================
// HOOKS PENTRU COMPONENTE (BACKWARD COMPATIBILITY)
// =============================================================================

/** Hook pentru data curentă completă (BACKWARD COMPATIBILITY) */
export const useCurrentDateTime = () => useGlobalDateStore((state) => state.currentDateTime);

/** Hook pentru data curentă UTC (NEW) */
export const useCurrentDateTimeUTC = () => useGlobalDateStore((state) => state.currentDateTimeUTC);

/** Hook pentru data curentă în timezone local (NEW) */
export const useCurrentDateTimeLocal = () => useGlobalDateStore((state) => state.currentDateTimeLocal);

// =============================================================================
// BACKWARD COMPATIBILITY EXPORTS - REMOVED to fix circular dependency
// =============================================================================

// ❌ REMOVED: Re-exports from dateHelpers cause circular dependency in production
// Use direct imports from '../utils/dateHelpers' instead
// This fixes "Cannot access 'A' before initialization" production error

// =============================================================================
// CLEANUP ÎN TIMPUL UNMOUNT-ULUI
// =============================================================================
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (clockTimer) clearInterval(clockTimer);
  });
}
